import {
  ControlledDateInput,
  ControlledInput,
} from "components/Card/ControlledInput";
import { Checkbox, MaskedInput } from "components/inputs";
import ControlledSelect from "components/inputs/Select/ControlledSelect";
import type {
  Control,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import type { ConstantTypes } from "types/DataTypes";

import type { LienFormValues } from "../../../LienTypes";

type DemandSectionProps = {
  control: Control<LienFormValues>;
  getValues: UseFormGetValues<LienFormValues>;
  setValue: UseFormSetValue<LienFormValues>;
  handleFieldChange: (field: string | null, value: unknown) => Promise<void>;
  constants?: ConstantTypes;
};

const DemandSection = ({
  control,
  getValues,
  setValue,
  handleFieldChange,
  constants,
}: DemandSectionProps) => {
  const demandReceived = getValues("demand_received") ?? false;
  const appealResultOptions = constants?.AppealResultChoices || [];
  return (
    <>
      <div className="header-primary flex jc-sb ai-c">
        <div className="text-sm text-gray-500">Demand</div>
      </div>

      <Checkbox
        name="demand_received"
        label="Demand Received"
        checked={getValues("demand_received")}
        required={false}
        onChange={async (e) => {
          setValue("demand_received", e.target.checked);
          if (!e.target.checked) {
            setValue("demand_amount", null);
            setValue("demand_appeal_by_date", null);
            setValue("demand_appeal_date", null);
            setValue("demand_closure_date", null);
            setValue("demand_date_issued", null);
            setValue("demand_pay_by_date", null);
            setValue("appeal_result", null);
            setValue("demand_appeal_result_dollar_amount", null);
            setValue("appeal_savings", null);
            setValue("noitr_received", false);
            setValue("noitr_date", null);
            await handleFieldChange(null, {
              demand_received: false,
              demand_amount: null,
              demand_amount_due: null,
              demand_appeal_by_date: null,
              demand_appeal_date: null,
              demand_closure_date: null,
              demand_date_issued: null,
              demand_pay_by_date: null,
              appeal_result: null,
              demand_appeal_result_dollar_amount: null,
              appeal_savings: null,
              noitr_received: false,
              noitr_date: null,
            });
          } else {
            await handleFieldChange("demand_received", e.target.checked);
          }
        }}
      />

      {demandReceived && (
        <>
          <ControlledDateInput
            control={control}
            name="demand_date_issued"
            label="Demand Date"
            errors={{}}
            allowFutureDates={true}
            onSave={(value) => handleFieldChange("demand_date_issued", value)}
          />

          <ControlledInput
            control={control}
            name="demand_amount"
            label="Demand Amount"
            errors={{}}
            onSave={(value) => handleFieldChange("demand_amount", value)}
            componentType={MaskedInput}
            mask="dollar"
          />

          <ControlledInput
            control={control}
            name="demand_total_disputed"
            label="Demand Total Disputed"
            errors={{}}
            onSave={(value) =>
              handleFieldChange("demand_total_disputed", value)
            }
            componentType={MaskedInput}
            mask="dollar"
            readOnly
          />

          <ControlledInput
            control={control}
            name="demand_amount_due"
            label="Demand Amount Due"
            errors={{}}
            onSave={(value) => handleFieldChange("demand_amount_due", value)}
            componentType={MaskedInput}
            mask="dollar"
            readOnly
          />

          <ControlledDateInput
            control={control}
            name="demand_pay_by_date"
            label="Pay By Date"
            errors={{}}
            allowFutureDates={true}
            onSave={(value) => handleFieldChange("demand_pay_by_date", value)}
          />

          <ControlledDateInput
            control={control}
            name="demand_appeal_by_date"
            label="Appeal By Date"
            errors={{}}
            allowFutureDates={true}
            onSave={(value) =>
              handleFieldChange("demand_appeal_by_date", value)
            }
          />

          <ControlledDateInput
            control={control}
            name="demand_appeal_date"
            label="Appeal Date"
            errors={{}}
            allowFutureDates={true}
            onSave={(value) => handleFieldChange("demand_appeal_date", value)}
          />

          <ControlledSelect<LienFormValues>
            control={control}
            name="appeal_result"
            label="Appeal Result"
            errors={{}}
            options={appealResultOptions}
            onSave={(value) => {
              return handleFieldChange("appeal_result", value?.value);
            }}
          />
          <ControlledDateInput
            control={control}
            name="appeal_result_date"
            label="Appeal Result Date"
            errors={{}}
            allowFutureDates={true}
            onSave={(value) => handleFieldChange("appeal_result_date", value)}
          />

          <ControlledInput
            control={control}
            name="demand_appeal_result_dollar_amount"
            label="Demand Appeal Result Dollar Amount"
            errors={{}}
            onSave={(value) =>
              handleFieldChange("demand_appeal_result_dollar_amount", value)
            }
            componentType={MaskedInput}
            mask="dollar"
          />

          <ControlledInput
            control={control}
            name="appeal_savings"
            label="Appeal Savings"
            errors={{}}
            onSave={(value) => handleFieldChange("appeal_savings", value)}
            componentType={MaskedInput}
            mask="dollar"
            readOnly
          />

          <ControlledDateInput
            control={control}
            name="demand_closure_date"
            label="Closure Date"
            errors={{}}
            allowFutureDates={true}
            onSave={(value) => handleFieldChange("demand_closure_date", value)}
          />

          <Checkbox
            name="noitr_received"
            label="NOITR Received"
            checked={getValues("noitr_received")}
            required={false}
            onChange={async (e) => {
              setValue("noitr_received", e.target.checked);
              if (!e.target.checked) {
                setValue("noitr_date", null);
                await handleFieldChange(null, {
                  noitr_received: false,
                  noitr_date: null,
                });
              } else {
                await handleFieldChange("noitr_received", e.target.checked);
              }
            }}
          />

          {getValues("noitr_received") && (
            <ControlledDateInput
              control={control}
              name="noitr_date"
              label="NOITR Date"
              errors={{}}
              allowFutureDates={true}
              onSave={(value) => handleFieldChange("noitr_date", value)}
            />
          )}
        </>
      )}
    </>
  );
};

export default DemandSection;
